import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, ReferenceLine,
} from 'recharts'

const fmtPct = (v: any) => v != null ? `${(v * 100).toFixed(2)}%` : '-'
const fmtPctSh = (v: any) => v != null ? `${(v * 100).toFixed(1)}%` : '-'

const MONTH_LABELS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function heatColor(v: number | null): string {
  if (v == null) return 'bg-gray-50'
  const pct = v * 100
  if (pct >= 2) return 'bg-green-600 text-white'
  if (pct >= 1) return 'bg-green-400 text-white'
  if (pct >= 0.5) return 'bg-green-300'
  if (pct >= 0) return 'bg-green-100'
  if (pct >= -0.5) return 'bg-red-100'
  if (pct >= -1) return 'bg-red-300'
  if (pct >= -2) return 'bg-red-400 text-white'
  return 'bg-red-600 text-white'
}

type Tab = 'summary' | 'heatmap' | 'attribution' | 'monthly'

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Multi-Period Summary' },
  { key: 'heatmap', label: 'Return Heatmap' },
  { key: 'attribution', label: 'Attribution' },
  { key: 'monthly', label: 'Monthly Returns' },
]

export default function PortfolioReturns() {
  const [tab, setTab] = useState<Tab>('summary')

  const { data, isLoading } = useQuery({
    queryKey: ['portfolio-returns-monitoring'],
    queryFn: () => api.get('/portfolio/returns-monitoring').then(r => r.data),
  })

  const fundNames: string[] = data?.fund_names || []

  // Chart data for monthly returns
  const monthlyChartData = useMemo(() => {
    if (!data?.monthly) return []
    return data.monthly.map((m: any) => ({
      date: m.date?.slice(0, 7) || m.date,
      return: m.portfolio_return != null ? m.portfolio_return * 100 : 0,
      nav: m.portfolio_nav,
    }))
  }, [data?.monthly])

  // Last 24 months for bar chart
  const recentMonthly = useMemo(() => {
    return monthlyChartData.slice(-24)
  }, [monthlyChartData])

  if (isLoading) {
    return <div className="flex justify-center p-8"><span className="spinner" /></div>
  }

  if (!data || !data.summary?.length) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Portfolio Returns</h1>
        <div className="text-center text-muted-foreground p-8">No portfolio return data available</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Portfolio Returns</h1>
          <p className="text-muted-foreground">
            Return monitoring &middot; {data.total_months} months &middot; {data.start_date?.slice(0, 7)} to {data.end_date?.slice(0, 7)}
          </p>
        </div>
      </div>

      {/* ── Quick Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {data.summary.map((s: any) => (
          <div key={s.period} className="bg-card border border-border rounded-lg p-3">
            <div className="text-xs text-muted-foreground">{s.period}</div>
            <div className={`text-lg font-bold font-mono ${s.portfolio != null && s.portfolio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {fmtPct(s.portfolio)}
            </div>
          </div>
        ))}
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════ MULTI-PERIOD SUMMARY TAB ═══════ */}
      {tab === 'summary' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold">Multi-Period Return Summary</h2>
            <p className="text-xs text-muted-foreground mt-1">Cumulative returns for portfolio and each fund</p>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-card z-10">Period</th>
                  <th className="text-right font-bold">Portfolio</th>
                  <th className="text-right">Ann. Return</th>
                  <th className="text-right">Ann. Vol</th>
                  {fundNames.map(f => (
                    <th key={f} className="text-right" title={f}>
                      {f.length > 18 ? f.slice(0, 15) + '...' : f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.summary.map((s: any) => (
                  <tr key={s.period}>
                    <td className="font-semibold sticky left-0 bg-card z-10">{s.period}</td>
                    <td className={`text-right font-mono font-bold ${s.portfolio != null && s.portfolio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtPct(s.portfolio)}
                    </td>
                    <td className="text-right font-mono text-muted-foreground">{fmtPct(s.portfolio_ann)}</td>
                    <td className="text-right font-mono text-muted-foreground">{fmtPct(s.portfolio_vol)}</td>
                    {fundNames.map(f => {
                      const v = s.funds?.[f]
                      return (
                        <td key={f} className={`text-right font-mono ${v != null && v >= 0 ? 'text-green-600' : v != null ? 'text-red-600' : ''}`}>
                          {fmtPct(v)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════ HEATMAP TAB ═══════ */}
      {tab === 'heatmap' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold">Monthly Return Heatmap</h2>
            <p className="text-xs text-muted-foreground mt-1">Portfolio-level monthly returns by year</p>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="text-xs border-collapse w-full">
              <thead>
                <tr>
                  <th className="p-2 text-left">Year</th>
                  {MONTH_LABELS.slice(1).map(m => (
                    <th key={m} className="p-2 text-center min-w-[52px]">{m}</th>
                  ))}
                  <th className="p-2 text-center font-bold min-w-[60px]">YTD</th>
                </tr>
              </thead>
              <tbody>
                {data.heatmap.map((row: any) => (
                  <tr key={row.year}>
                    <td className="p-2 font-semibold">{row.year}</td>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
                      const v = row.months?.[m]
                      return (
                        <td key={m} className={`p-2 text-center font-mono border border-border/30 ${heatColor(v)}`}>
                          {v != null ? fmtPctSh(v) : ''}
                        </td>
                      )
                    })}
                    <td className={`p-2 text-center font-mono font-bold border border-border/30 ${heatColor(row.ytd)}`}>
                      {fmtPctSh(row.ytd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 mt-3 text-[10px] text-muted-foreground items-center">
              <span>Color scale:</span>
              <span className="inline-block w-3 h-3 bg-red-600 rounded" /> &lt; -2%
              <span className="inline-block w-3 h-3 bg-red-300 rounded" /> -2% to -0.5%
              <span className="inline-block w-3 h-3 bg-red-100 rounded" /> -0.5% to 0
              <span className="inline-block w-3 h-3 bg-green-100 rounded" /> 0 to 0.5%
              <span className="inline-block w-3 h-3 bg-green-300 rounded" /> 0.5% to 1%
              <span className="inline-block w-3 h-3 bg-green-600 rounded" /> &gt; 2%
            </div>
          </div>
        </div>
      )}

      {/* ═══════ ATTRIBUTION TAB ═══════ */}
      {tab === 'attribution' && (
        <div className="space-y-6">
          {/* Attribution table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold">Return Attribution</h2>
              <p className="text-xs text-muted-foreground mt-1">Each fund's contribution to portfolio returns (weight &times; return)</p>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th className="text-right">Weight</th>
                    <th className="text-right">Last Month</th>
                    <th className="text-right">Contribution</th>
                    <th className="text-right">YTD Return</th>
                    <th className="text-right">YTD Contrib</th>
                  </tr>
                </thead>
                <tbody>
                  {data.attribution.map((a: any) => (
                    <tr key={a.fund_name}>
                      <td className="font-medium max-w-[220px] truncate" title={a.fund_name}>{a.fund_name}</td>
                      <td className="text-right font-mono">{fmtPct(a.weight)}</td>
                      <td className={`text-right font-mono ${a.last_return != null && a.last_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtPct(a.last_return)}
                      </td>
                      <td className={`text-right font-mono font-semibold ${a.last_contribution != null && a.last_contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtPct(a.last_contribution)}
                      </td>
                      <td className={`text-right font-mono ${a.ytd_return != null && a.ytd_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtPct(a.ytd_return)}
                      </td>
                      <td className={`text-right font-mono font-semibold ${a.ytd_contribution != null && a.ytd_contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtPct(a.ytd_contribution)}
                      </td>
                    </tr>
                  ))}
                  {/* Total row */}
                  <tr className="border-t-2 border-border font-bold">
                    <td>Portfolio Total</td>
                    <td className="text-right font-mono">100.00%</td>
                    <td className="text-right font-mono">{fmtPct(data.summary?.find((s: any) => s.period === 'MTD')?.portfolio)}</td>
                    <td className="text-right font-mono">{fmtPct(data.summary?.find((s: any) => s.period === 'MTD')?.portfolio)}</td>
                    <td className="text-right font-mono">{fmtPct(data.summary?.find((s: any) => s.period === 'YTD')?.portfolio)}</td>
                    <td className="text-right font-mono">{fmtPct(data.summary?.find((s: any) => s.period === 'YTD')?.portfolio)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Attribution bar chart - last month */}
          {data.attribution?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Last Month Attribution (bps)</h2>
              <ResponsiveContainer width="100%" height={Math.max(300, data.attribution.length * 32)}>
                <BarChart
                  data={data.attribution.map((a: any) => ({
                    name: a.fund_name.length > 25 ? a.fund_name.slice(0, 22) + '...' : a.fund_name,
                    fullName: a.fund_name,
                    contribution: a.last_contribution != null ? a.last_contribution * 10000 : 0,
                  }))}
                  layout="vertical" margin={{ left: 140, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v.toFixed(0)} bps`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip content={({ payload }) => {
                    if (!payload?.length) return null
                    const d = payload[0].payload
                    return (
                      <div className="bg-card border border-border rounded p-2 text-xs shadow-lg">
                        <div className="font-semibold">{d.fullName}</div>
                        <div>Contribution: {d.contribution.toFixed(1)} bps</div>
                      </div>
                    )
                  }} />
                  <ReferenceLine x={0} stroke="hsl(214, 32%, 60%)" />
                  <Bar dataKey="contribution" fill="hsl(217, 91%, 50%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ═══════ MONTHLY RETURNS TAB ═══════ */}
      {tab === 'monthly' && (
        <div className="space-y-6">
          {/* Monthly returns bar chart */}
          {recentMonthly.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Monthly Portfolio Returns (last 24m)</h2>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={recentMonthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={1} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                    formatter={(v: number) => [`${v.toFixed(2)}%`, 'Return']}
                  />
                  <ReferenceLine y={0} stroke="hsl(214, 32%, 60%)" />
                  <Bar dataKey="return" fill="hsl(217, 91%, 50%)" radius={[2, 2, 0, 0]}
                    /* Color positive/negative */
                    shape={(props: any) => {
                      const { x, y, width, height, value } = props
                      const color = value >= 0 ? 'hsl(142, 71%, 45%)' : 'hsl(0, 72%, 51%)'
                      return <rect x={x} y={y} width={width} height={height} fill={color} rx={2} />
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Cumulative NAV line chart */}
          {monthlyChartData.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Cumulative Portfolio NAV</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => v.toFixed(2)} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                    formatter={(v: number) => [v.toFixed(4), 'NAV']}
                  />
                  <Line type="monotone" dataKey="nav" stroke="hsl(217, 91%, 50%)" strokeWidth={2} dot={false} name="NAV" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly returns data table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <h2 className="font-semibold">Monthly Returns Detail</h2>
              <span className="text-xs text-muted-foreground">{data.monthly?.length || 0} months</span>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="data-table text-xs">
                <thead className="sticky top-0 bg-card z-10">
                  <tr>
                    <th className="sticky left-0 bg-card z-20">Date</th>
                    <th className="text-right font-bold">Portfolio</th>
                    <th className="text-right">NAV</th>
                    {fundNames.map(f => (
                      <th key={f} className="text-right" title={f}>
                        {f.length > 15 ? f.slice(0, 12) + '...' : f}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...(data.monthly || [])].reverse().map((m: any) => (
                    <tr key={m.date}>
                      <td className="font-mono sticky left-0 bg-card z-10">{m.date?.slice(0, 7)}</td>
                      <td className={`text-right font-mono font-bold ${m.portfolio_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtPct(m.portfolio_return)}
                      </td>
                      <td className="text-right font-mono text-muted-foreground">{m.portfolio_nav?.toFixed(4)}</td>
                      {fundNames.map(f => {
                        const v = m.contributions?.[f]
                        return (
                          <td key={f} className={`text-right font-mono ${v != null && v >= 0 ? 'text-green-600' : v != null ? 'text-red-600' : ''}`}>
                            {v != null ? `${(v * 10000).toFixed(1)}` : '-'}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-2 text-[10px] text-muted-foreground border-t border-border">
              Fund columns show contributions in basis points (bps)
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
